import { createClient, OASClient, type NormalizeOAS } from 'fets';
import type openapi from './openapi';
import { AuthPlugin } from './fetsAuthPlugin';

/**
 * KNOWN INTENTIONAL fets leak (escape hatch).
 *
 * `ApiClient` is the raw fets OpenAPI client. Unlike every other public type it
 * exposes fets-derived types directly, which the wrapper pattern normally
 * forbids. It stays this way because the manager app calls endpoints directly
 * off this client (e.g. `apiClient['/api/...'].get(...)`) in
 * its store middleware and repositories, bypassing the create<Name>() wrappers.
 *
 * Do NOT add new direct usages. Prefer a create<Name>() wrapper that maps to
 * domain types (see server-api/aiRequests/interpretImageKeywords.ts). Once the
 * remaining manager direct calls are wrapped, this can become an opaque handle.
 */
export type ApiClient = OASClient<NormalizeOAS<typeof openapi>>;

const _createApiClient = (backendUrl: string, provideAuthTokenFunc: () => Promise<string>): ApiClient => {
  const apiClient = createClient<NormalizeOAS<typeof openapi>>({
    endpoint: backendUrl,
    plugins: [AuthPlugin(provideAuthTokenFunc)],
  });

  return apiClient;
};

/**
 * Create a authenticated API client for the backend.
 */
export const createApiClient = (backendUrl: string, provideFunc: () => Promise<string>): ApiClient => {
  return _createApiClient(backendUrl, provideFunc);
};
